const VERIFIER_KEY = "atlas_pkce_verifier";
const STATE_KEY    = "atlas_pkce_state";
const NONCE_KEY    = "atlas_pkce_nonce";

function base64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function generateVerifier(): string {
  return base64url(crypto.getRandomValues(new Uint8Array(32)).buffer);
}

export async function deriveChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(digest);
}

export function generateState(): string {
  return crypto.randomUUID();
}

export async function beginPkceLogin(
  authIssuer: string,
  clientId: string,
  redirectUri: string,
): Promise<void> {
  const discovered = await fetch(`${authIssuer}/.well-known/openid-configuration`).then((response) => {
    if (!response.ok) throw new Error("Authentication discovery is unavailable");
    return response.json() as Promise<{ authorization_endpoint: string }>;
  });
  const verifier   = generateVerifier();
  const challenge  = await deriveChallenge(verifier);
  const state      = generateState();
  const nonce      = generateState();

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(NONCE_KEY, nonce);

  const url = new URL(discovered.authorization_endpoint);
  url.searchParams.set("response_type",          "code");
  url.searchParams.set("client_id",              clientId);
  url.searchParams.set("redirect_uri",           redirectUri);
  url.searchParams.set("scope",                  "openid profile email offline_access mavr:suite:read");
  url.searchParams.set("resource",               "pks-bff");
  url.searchParams.set("code_challenge",         challenge);
  url.searchParams.set("code_challenge_method",  "S256");
  url.searchParams.set("state",                  state);
  url.searchParams.set("nonce",                  nonce);

  window.location.href = url.toString();
}

export interface OidcTokenResponse {
  access_token:  string;
  refresh_token?: string;
  token_type:    string;
  expires_in:    number;
  id_token?:     string;
}

export async function exchangeCode(
  _authIssuer: string,
  _clientId: string,
  code: string,
  state: string,
  redirectUri: string,
): Promise<OidcTokenResponse> {
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier      = sessionStorage.getItem(VERIFIER_KEY);

  if (!expectedState || !verifier || state !== expectedState) {
    throw new Error("Invalid OAuth state — possible CSRF");
  }
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  const res = await fetch("/auth/oidc/callback", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
    code,
      codeVerifier: verifier,
      redirectUri,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token exchange failed: ${text}`);
  }
  return res.json() as Promise<OidcTokenResponse>;
}

/**
 * Validate the ID token the issuer returned with the code exchange. The access
 * token itself is re-validated server-side by pks-bff on every API call — this
 * check exists so a mismatched/misrouted issuer response fails here, in the
 * browser, before it poisons the session store.
 *
 * Tolerates issuers that do not mint an ID token (returns null, caller skips).
 * The one-time nonce is consumed either way so it can never be replayed.
 */
export function validateIdToken(
  idToken: string | undefined,
  authIssuer: string,
  clientId: string,
): Record<string, unknown> | null {
  const expectedNonce = sessionStorage.getItem(NONCE_KEY);
  sessionStorage.removeItem(NONCE_KEY);
  if (!idToken) return null;
  const claims = decodeJwtPayload(idToken);
  if (claims.iss !== authIssuer) {
    throw new Error("ID token issuer mismatch");
  }
  const aud = claims.aud;
  const audiences = Array.isArray(aud) ? aud : [aud];
  if (!audiences.includes(clientId)) {
    throw new Error("ID token audience mismatch");
  }
  if (!expectedNonce || claims.nonce !== expectedNonce) {
    throw new Error("ID token nonce mismatch — possible replay");
  }
  return claims;
}

/** Decode a JWT payload without verification (claims already verified server-side by pks-bff). */
export function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split(".");
  if (parts.length < 2) throw new Error("Invalid JWT");
  const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
  return JSON.parse(json) as Record<string, unknown>;
}
